import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import Button from "../Button";

interface MultiFileDropzoneProps {
    onFilesSelected: (files: File[]) => void;
    dark?: boolean;
    buttonType?: string;
    text?: string | React.ReactNode;
    buttonText?: string;
    acceptedFileTypes?: string;
    selectedFiles?: File[];
    jobType?: "images" | "videos";
    error?: boolean;
    className?: string;
}

const useVideoThumbnail = (file: File | null): string | null => {
    const [thumbnail, setThumbnail] = useState<string | null>(null);

    useEffect(() => {
        if (!file || !file.type.startsWith("video/")) {
            setThumbnail(null);
            return;
        }

        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.src = videoUrl;
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.preload = "metadata";

        const handleLoadedMetadata = () => {
            video.currentTime = 0.1;
        };

        const handleSeeked = () => {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                ctx.drawImage(video, 0, 0);
                setThumbnail(canvas.toDataURL("image/jpeg"));
            }
            URL.revokeObjectURL(videoUrl);
        };

        const handleError = () => {
            setThumbnail(null);
            URL.revokeObjectURL(videoUrl);
        };

        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("seeked", handleSeeked);
        video.addEventListener("error", handleError);

        return () => {
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            video.removeEventListener("seeked", handleSeeked);
            video.removeEventListener("error", handleError);
            URL.revokeObjectURL(videoUrl);
        };
    }, [file]);

    return thumbnail;
};

const VideoPreview: React.FC<{ file: File; index: number }> = ({ file, index }) => {
    const thumbnail = useVideoThumbnail(file);

    if (thumbnail) {
        return (
            <img
                src={thumbnail}
                alt={`Video preview ${index + 1}`}
                className="h-full w-full object-cover"
            />
        );
    }

    return (
        <div className="h-full w-full bg-gradient-to-br from-purboflow-50 to-purboflow-100"></div>
    );
};

export function MultiFileDropzone({
    onFilesSelected,
    dark = false,
    buttonType,
    text = "Drop files here or",
    buttonText = "Select Files",
    acceptedFileTypes = "*/*",
    selectedFiles = [],
    jobType = "images",
    error = false,
    className
}: MultiFileDropzoneProps) {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const bType = buttonType ?? (dark ? "dark-secondary" : "secondary");

    const isFileTypeValid = (file: File): boolean => {
        if (jobType === "images") {
            return file.type.startsWith("image/");
        } else {
            return file.type.startsWith("video/");
        }
    };

    const processItems = async (items: DataTransferItemList): Promise<File[]> => {
        const allFiles: File[] = [];

        const readEntry = async (entry: FileSystemEntry): Promise<void> => {
            return new Promise((resolve) => {
                if (entry.isFile) {
                    (entry as FileSystemFileEntry).file((file) => {
                        if (isFileTypeValid(file)) {
                            allFiles.push(file);
                        }
                        resolve();
                    });
                } else if (entry.isDirectory) {
                    const reader = (entry as FileSystemDirectoryEntry).createReader();
                    reader.readEntries(async (entries) => {
                        await Promise.all(entries.map(readEntry));
                        resolve();
                    });
                }
            });
        };

        await Promise.all(
            Array.from(items).map((item) => {
                const entry = item.webkitGetAsEntry();
                if (entry) {
                    return readEntry(entry);
                }
                return Promise.resolve();
            })
        );

        return allFiles;
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        let newFiles: File[] = [];

        // Handle folder drops
        if (e.dataTransfer.items) {
            newFiles = await processItems(e.dataTransfer.items);
        } else {
            // Fallback for regular file drops
            newFiles = Array.from(e.dataTransfer.files).filter(isFileTypeValid);
        }

        // Combine with existing files, removing duplicates by name
        const existingFileNames = new Set(selectedFiles.map((f) => f.name));
        const uniqueNewFiles = newFiles.filter((file) => !existingFileNames.has(file.name));

        onFilesSelected([...selectedFiles, ...uniqueNewFiles]);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFiles = Array.from(e.target.files || []).filter(isFileTypeValid);

        // Combine with existing files, removing duplicates by name
        const existingFileNames = new Set(selectedFiles.map((f) => f.name));
        const uniqueNewFiles = newFiles.filter((file) => !existingFileNames.has(file.name));

        onFilesSelected([...selectedFiles, ...uniqueNewFiles]);
    };

    const handleDropzoneClick = (e: React.MouseEvent) => {
        // Don't trigger if clicking on an interactive element (button, input, etc.)
        const target = e.target as HTMLElement;
        if (
            target.tagName === "BUTTON" ||
            target.tagName === "INPUT" ||
            target.closest("button") ||
            target.closest("label")
        ) {
            return;
        }

        // Trigger file input click
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div
            className={classNames(
                "flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-all duration-200",
                "min-h-[120px] flex-1",
                isDragging
                    ? "scale-[1.02] border-purboflow-400 bg-purboflow-50 shadow-lg"
                    : error
                      ? "border-pink-300 bg-pink-50"
                      : "cursor-pointer border-gray-300 bg-gray-50/50 hover:border-gray-400 hover:bg-gray-100/50",
                dark && "border-gray-600 bg-gray-800",
                className
            )}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleDropzoneClick}
        >
            <input
                ref={fileInputRef}
                id="multiFileInput"
                type="file"
                multiple
                accept={acceptedFileTypes}
                onChange={handleFileInput}
                className="hidden"
            />
            <label
                className="flex w-full cursor-pointer flex-col items-center justify-center gap-4"
                htmlFor="multiFileInput"
            >
                {selectedFiles.length > 0 ? (
                    <div className="flex w-full flex-col items-center gap-6">
                        <div className="flex items-center gap-3">
                            {selectedFiles.slice(0, 3).map((file, index) => (
                                <div
                                    key={`${file.name}-${index}`}
                                    className="relative h-20 w-20 overflow-hidden rounded-lg border-2 border-gray-200 shadow-sm"
                                >
                                    {jobType === "images" && file.type.startsWith("image/") ? (
                                        <img
                                            src={URL.createObjectURL(file)}
                                            alt={`Preview ${index + 1}`}
                                            className="h-full w-full object-cover"
                                            onLoad={(e) =>
                                                URL.revokeObjectURL(
                                                    (e.target as HTMLImageElement).src
                                                )
                                            }
                                        />
                                    ) : jobType === "videos" && file.type.startsWith("video/") ? (
                                        <VideoPreview file={file} index={index} />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                                            <i className="far fa-image text-2xl text-gray-500"></i>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {selectedFiles.length > 3 && (
                                <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                                    <div className="text-center">
                                        <div className="text-lg font-bold text-gray-600">
                                            +{selectedFiles.length - 3}
                                        </div>
                                        <div className="text-xs text-gray-500">more</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="text-center">
                            <div className="mb-2 flex items-center justify-center gap-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purboflow-100">
                                    <i
                                        className={classNames(
                                            "text-sm",
                                            jobType === "videos"
                                                ? "far fa-video text-purboflow-600"
                                                : "far fa-images text-purboflow-600"
                                        )}
                                    ></i>
                                </div>
                                <span className="text-lg text-gray-600">
                                    {jobType.charAt(0).toUpperCase() + jobType.slice(1)} Selected
                                </span>
                            </div>
                            <div className="text-sm text-gray-500">
                                Drop more files or folders to add to selection
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="mb-6 flex flex-col items-center gap-3">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-purboflow-100">
                                <i
                                    className={classNames(
                                        "text-2xl",
                                        jobType === "videos"
                                            ? "far fa-video text-purboflow-600"
                                            : "far fa-images text-purboflow-600"
                                    )}
                                ></i>
                            </div>
                            <div className="text-center">
                                <div className="mb-1 text-lg font-semibold text-gray-900">
                                    {jobType === "videos" ? "Upload Videos" : "Upload Images"}
                                </div>
                                <div className="text-sm text-gray-600">{text}</div>
                            </div>
                        </div>

                        <div className="z-10 flex w-full items-center justify-center">
                            <div
                                className={classNames(
                                    "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200",
                                    "bg-purboflow-600 text-white hover:bg-purboflow-700 hover:shadow-md",
                                    "focus:outline-none focus:ring-2 focus:ring-purboflow-500 focus:ring-offset-2"
                                )}
                            >
                                <i className="far fa-upload"></i>
                                <span>{buttonText}</span>
                            </div>
                        </div>

                        {error && (
                            <div className="mt-4 flex items-center gap-2 rounded-lg bg-pink-50 px-3 py-2">
                                <i className="far fa-exclamation-circle text-pink-700"></i>
                                <span className="text-sm text-pink-700">
                                    Please upload at least one file
                                </span>
                            </div>
                        )}
                    </>
                )}
            </label>
        </div>
    );
}
